import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { PeopleMembersAnalyticsEventProperties } from 'src/app/PeopleMembers/PeopleMembers'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BoxAndLinks from 'src/components/BoxAndLinks/BoxAndLinks'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import PeopleBox from 'src/components/PeopleBox/PeopleBox'
import { OrganizationRoles } from 'src/enums'
import { OrgInviteModel, UserConnectionModel } from 'src/models/people.models'
import { UserListModel } from 'src/models/user.models'
import { peopleMembersUrl } from 'src/navigation/urls'
import { SearchQueryParams } from 'src/redux/createRootReducer'
import { selectInvitedToOrgListModelsFromState } from 'src/redux/selectors/data/invitedToOrg.selectors'
import { selectMembersListModelsFromState } from 'src/redux/selectors/data/members.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

interface PeopleBoxAndLinksProps {
  contactListModels?: UserConnectionModel[]
  loading?: boolean
  peopleInfiniteScrollProps: PeopleInfiniteScrollProps
  noPeopleComponent?: React.ReactElement
  showFilters?: boolean
  stateQueryParams: SearchQueryParams
  setStateQueryParams: (SearchQueryParams: SearchQueryParams) => void
  keywordFilter?: string
  setKeywordFilter?: (keyword: string) => void
  organizationRolesFilter?: Set<string>
  setOrganizationRoleFilter?: (organizationRoles: Set<string>) => void
  hideDeactivatedUsers?: boolean
  setHideDeactivatedUsers?: (hideDeactivatedUsers: boolean) => void
  handleOrganizationRoleSelection?: (role: string) => void
  setSort: (sortField: string) => void
  sortBy?: string
  sortDescending?: boolean
  initialLoad: boolean
  setInitialLoad: (initialLoad: boolean) => void
}

export interface PeopleFilters {
  keyword: string
  organizationRoles: OrganizationRoles[]
  showDeactivatedUsers?: boolean
  sortBy?: string
  sortDescending: boolean
}

export interface PeopleFiltersBody {
  search?: string
  organizationRoles?: string[]
  sortField?: string
  page?: number
  sortDirection?: string
  showDeactivatedUsers?: boolean
}

export interface PeopleInfiniteScrollProps {
  fetch: (filtersBody: PeopleFiltersBody, page?: number) => void
  page?: number
  loading: boolean
  complete: boolean
}

const PeopleBoxAndLinks: React.FC<PeopleBoxAndLinksProps> = (props) => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const memberListModels = useSelector(selectMembersListModelsFromState)
  const invitedListModels = useSelector(selectInvitedToOrgListModelsFromState)

  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    PeopleMembersAnalyticsEventProperties | undefined
  >(undefined)

  // give the embedded DataGrid a fixed height so it owns its own internal
  // scroll/infinite-scroll (mirrors VirtualCardsAllCards)
  const getViewportHeight = (): number =>
    typeof window !== 'undefined' ? window.innerHeight : 0
  const [viewportHeight, setViewportHeight] = useState(getViewportHeight)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const onResize = (): void => setViewportHeight(getViewportHeight())
    window.addEventListener('resize', onResize)
    return (): void => window.removeEventListener('resize', onResize)
  }, [])
  const clientHeight = Math.max(0, viewportHeight - 150)

  const people = ():
    | UserListModel[]
    | UserConnectionModel[]
    | OrgInviteModel[] => {
    return matchPath(calculatedBackgroundLocation.pathname, peopleMembersUrl())
      ? Object.values(memberListModels)
      : Object.values(invitedListModels)
  }

  // const reportType = (): PeopleSubSections => {
  //   switch (calculatedBackgroundLocation.pathname) {
  //     case peopleMembersUrl():
  //     default:
  //       return PeopleSubSections.members
  //     case peopleContactsUrl():
  //       return PeopleSubSections.contacts
  //     case peopleInvitedToOrgUrl():
  //       return PeopleSubSections.invites
  //   }
  // }

  // const downloadPeopleString = (): string => {
  //   switch (calculatedBackgroundLocation.pathname) {
  //     case peopleContactsUrl():
  //       return 'contacts'
  //     case peopleInvitedToOrgUrl():
  //       return 'invitees'
  //     case peopleMembersUrl():
  //     default:
  //       return 'members'
  //   }
  // }

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('org members filter applied', {
        role: selectedFilters.organizationRoles,
      })
    }
  }, [selectedFilters])

  return (
    <BackgroundPageContent noStackingIndex noPadding>
      {people().length > 0 ? (
        <BoxAndLinks>
          <PeopleBox
            peopleInfiniteScrollProps={props.peopleInfiniteScrollProps}
            loading={props.loading}
            showFilters={props.showFilters}
            stateQueryParams={props.stateQueryParams}
            setStateQueryParams={props.setStateQueryParams}
            keywordFilter={props.keywordFilter}
            setKeywordFilter={props.setKeywordFilter}
            organizationRolesFilter={props.organizationRolesFilter}
            setOrganizationRoleFilter={props.setOrganizationRoleFilter}
            handleOrganizationRoleSelection={
              props.handleOrganizationRoleSelection
            }
            hideDeactivatedUsers={props.hideDeactivatedUsers}
            setHideDeactivatedUsers={props.setHideDeactivatedUsers}
            setSort={props.setSort}
            sortBy={props.sortBy}
            sortDescending={props.sortDescending}
            initialLoad={props.initialLoad}
            setInitialLoad={props.setInitialLoad}
            clientHeight={clientHeight}
            setSelectedFilters={setSelectedFilters}
          />
        </BoxAndLinks>
      ) : props.loading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            width: 100%;
            flex-grow: 2;
          `}
        >
          <LoadingBackgroundPage />
        </div>
      ) : (
        props.noPeopleComponent
      )}
    </BackgroundPageContent>
  )
}

export default PeopleBoxAndLinks
